import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Assessment from './pages/Assessment';
import Workbooks from './pages/Workbooks';
import WorkbookRouter from './pages/WorkbookRouter';
import WinsWall from './pages/WinsWall';
import AccountSettings from './pages/AccountSettings';
import AdminDashboard from './pages/AdminDashboard';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import ResourcesHub from './pages/ResourcesHub';
import PromptLibrary from './pages/PromptLibrary';
import AgentLibrary from './pages/AgentLibrary';
import VerifyCertificate from './pages/VerifyCertificate';
import ForcePasswordChange from './pages/ForcePasswordChange';
import References from './pages/References';
import InviteParticipants from './pages/InviteParticipants';
import EmailTemplates from './pages/EmailTemplates';
import PrivacyStatement from './pages/PrivacyStatement';
import Exam from './pages/Exam';
import UseCaseFinder from './pages/UseCaseFinder';
import IdleTimeoutManager from './components/IdleTimeoutManager';
import './App.css';

const TCF_ADMIN_EMAILS = ['tamica@thecorporatefixers.com', 'admin@thecorporatefixers.com', 'tamica@searscoaching.com'];

const WORKBOOK_ROUTE = 'workbook';

// Turn the address-bar hash into a page (and workbook day, if any).
function parseRoute(hash) {
  const h = (hash || '').replace(/^#\/?/, '');
  if (!h) return { page: null, workbook: null };
  const parts = h.split('/');
  if (parts[0] === WORKBOOK_ROUTE && parts[1]) {
    const day = parseInt(parts[1], 10);
    if (day >= 1 && day <= 10) return { page: 'workbook-detail', workbook: { day } };
  }
  return { page: parts[0], workbook: null };
}

function routeToHash(page, wb) {
  if (page === 'workbook-detail' && wb && wb.day) return `#/${WORKBOOK_ROUTE}/${wb.day}`;
  return `#/${page}`;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('dashboard');
  const [selectedWorkbook, setSelectedWorkbook] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authView, setAuthView] = useState('login');
  const [winsUnread, setWinsUnread] = useState(0);
  const [winsSeenAt, setWinsSeenAt] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        if (TCF_ADMIN_EMAILS.includes(u.email)) {
          setUserRole('tcfadmin');
          setPage('dashboard');
          setLoading(false);
          return;
        }
        try {
          let profileSnap = await getDoc(doc(db, 'users', u.uid));
          if (!profileSnap.exists()) {
            await new Promise(r => setTimeout(r, 800));
            profileSnap = await getDoc(doc(db, 'users', u.uid));
          }
          if (profileSnap.exists()) {
            const profile = profileSnap.data();
            setUserProfile(profile);
            if (profile.adminRole === 'tcfadmin') {
              setUserRole('tcfadmin');
            } else if (profile.adminRole === 'companyadmin') {
              setUserRole('companyadmin');
            } else {
              // The companyAdmins collection is the source of truth the rules
              // check. A grant made before this person registered (or made
              // against a differently-cased email) lives only there, so honor
              // it here and backfill the profile for the dashboards.
              const grant = await getDoc(doc(db, 'companyAdmins', (u.email || '').toLowerCase()));
              if (grant.exists() && (grant.data().companyCodes || []).length > 0) {
                const codes = grant.data().companyCodes;
                setUserRole('companyadmin');
                try {
                  await setDoc(doc(db, 'users', u.uid), {
                    adminRole: 'companyadmin',
                    adminCompanyCode: codes[0],
                    adminCompany: grant.data().company || grant.data().companyName || profile.company || null,
                  }, { merge: true });
                } catch { /* routing already correct; backfill is best effort */ }
              } else {
                setUserRole('participant');
              }
            }
          } else {
            setUserRole('participant');
          }
        } catch {
          setUserRole('participant');
        }
        setPage('dashboard');
      } else {
        setUserRole(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Honor a deep link (e.g. opening #/workbook/3 in a new tab) once signed in.
  useEffect(() => {
    if (!user) return;
    const route = parseRoute(window.location.hash);
    if (route.page) {
      setPage(route.page);
      setSelectedWorkbook(route.workbook);
    }
  }, [user, userRole]);

  // Keep the view in sync with the address bar (links, back button, new tabs).
  useEffect(() => {
    const onHash = () => {
      const route = parseRoute(window.location.hash);
      if (route.page) {
        setPage(route.page);
        setSelectedWorkbook(route.workbook);
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Unread badge for the Wins Wall. "Unread" = posted or replied to since the
  // last time this person opened the wall, so nothing has to be marked read.
  useEffect(() => {
    if (userProfile && winsSeenAt === null) {
      setWinsSeenAt(userProfile.winsLastSeenAt?.toDate ? userProfile.winsLastSeenAt.toDate().getTime() : 0);
    }
  }, [userProfile, winsSeenAt]);

  useEffect(() => {
    if (page === 'wins') {
      setWinsUnread(0);
      setWinsSeenAt(Date.now());
    }
  }, [page]);

  useEffect(() => {
    if (!user || !userProfile?.company || winsSeenAt === null || page === 'wins') return;
    let cancelled = false;
    const countNew = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'wins'), where('company', '==', userProfile.company)));
        let n = 0;
        snap.docs.forEach(d => {
          const w = d.data();
          const posted = w.createdAt?.seconds ? w.createdAt.seconds * 1000 : 0;
          const replied = w.lastReplyAt?.seconds ? w.lastReplyAt.seconds * 1000 : 0;
          const liked = w.lastReactionAt?.seconds ? w.lastReactionAt.seconds * 1000 : 0;
          // Your own win counts only once somebody else replies or likes it.
          if (w.authorId === user.uid) {
            if (Math.max(replied, liked) > winsSeenAt) n += 1;
            return;
          }
          if (Math.max(posted, replied) > winsSeenAt) n += 1;
        });
        if (!cancelled) setWinsUnread(n);
      } catch { /* the badge is optional */ }
    };
    countNew();
    return () => { cancelled = true; };
  }, [user, userProfile, winsSeenAt, page]);

  // Public credential verification page: works with or without a login so
  // recruiters and managers can open shared links directly.
  const verifyMatch = window.location.hash.match(/^#\/?verify\/(.+)$/);
  if (verifyMatch) return <VerifyCertificate certId={decodeURIComponent(verifyMatch[1])} />;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#1B2A4A' }}>
      <div style={{ fontFamily: 'Playfair Display, serif', color: '#D4A847', fontSize: '24px' }}>Loading...</div>
    </div>
  );

  if (!user) {
    if (authView === 'register') return <Register onSwitch={() => setAuthView('login')} />;
    return <Login onSwitch={() => setAuthView('register')} />;
  }

  // An admin set them a temporary password: make them choose their own first.
  if (userProfile?.mustChangePassword) {
    return (
      <ForcePasswordChange
        user={user}
        onDone={() => setUserProfile(prev => ({ ...prev, mustChangePassword: false }))}
      />
    );
  }

  const nav = (p, wb = null) => {
    setPage(p);
    setSelectedWorkbook(wb);
    window.scrollTo(0, 0);
    const h = routeToHash(p, wb);
    if (window.location.hash !== h) window.location.hash = h;
  };

  if (userRole === 'tcfadmin') return (
    <>
      <AdminDashboard user={user} onNav={nav} />
      <IdleTimeoutManager />
    </>
  );

  if (userRole === 'companyadmin') {
    return (
      <div className="app">
        <TopNav user={user} onNav={nav} roleLabel="Company Admin" />
        <div className="app-layout">
          <Sidebar page={page} onNav={nav} isCompanyAdmin={true} winsUnread={winsUnread} />
          <main className="main-content">
            {page === 'dashboard' && <Dashboard user={user} onNav={nav} />}
            {page === 'assessment' && <Assessment user={user} onNav={nav} />}
            {page === 'exam' && <Exam user={user} onNav={nav} />}
            {page === 'workbooks' && <Workbooks user={user} onNav={nav} onSelect={(wb) => nav('workbook-detail', wb)} />}
            {page === 'workbook-detail' && <WorkbookRouter user={user} workbook={selectedWorkbook} onNav={nav} />}
            {page === 'usecasefinder' && <UseCaseFinder user={user} onNav={nav} />}
            {page === 'wins' && <WinsWall user={user} />}
            {page === 'account' && <AccountSettings user={user} onNav={nav} />}
            {page === 'teamdashboard' && <CompanyAdminDashboard user={user} userProfile={userProfile} onNav={nav} />}
            {page === 'invites' && <InviteParticipants user={user} mode="company" />}
            {page === 'emailtemplates' && <EmailTemplates user={user} mode="company" />}
            {page === 'resources' && <ResourcesHub user={user} />}
            {page === 'promptlibrary' && <PromptLibrary user={user} />}
            {page === 'agentlibrary' && <AgentLibrary user={user} />}
            {page === 'references' && <References />}
            {page === 'privacy' && <PrivacyStatement onNav={nav} />}
          </main>
        </div>
        <PortalFooter onNav={nav} />
        <IdleTimeoutManager />
      </div>
    );
  }

  return (
    <div className="app">
      <TopNav user={user} onNav={nav} roleLabel="Participant" />
      <div className="app-layout">
        <Sidebar page={page} onNav={nav} isCompanyAdmin={false} winsUnread={winsUnread} />
        <main className="main-content">
          {page === 'dashboard' && <Dashboard user={user} onNav={nav} />}
          {page === 'assessment' && <Assessment user={user} onNav={nav} />}
          {page === 'exam' && <Exam user={user} onNav={nav} />}
          {page === 'workbooks' && <Workbooks user={user} onNav={nav} onSelect={(wb) => nav('workbook-detail', wb)} />}
          {page === 'workbook-detail' && <WorkbookRouter user={user} workbook={selectedWorkbook} onNav={nav} />}
          {page === 'usecasefinder' && <UseCaseFinder user={user} onNav={nav} />}
          {page === 'wins' && <WinsWall user={user} />}
          {page === 'account' && <AccountSettings user={user} onNav={nav} />}
          {page === 'promptlibrary' && <PromptLibrary user={user} />}
          {page === 'agentlibrary' && <AgentLibrary user={user} />}
          {page === 'references' && <References />}
          {page === 'privacy' && <PrivacyStatement onNav={nav} />}
        </main>
      </div>
      <PortalFooter onNav={nav} />
      <IdleTimeoutManager />
    </div>
  );
}

function TopNav({ user, onNav, roleLabel }) {
  return (
    <nav className="top-nav">
      <div className="nav-logo">The Corporate <span>Fixers</span></div>
      <div className="nav-right">
        <div className="nav-user">{roleLabel ? `${roleLabel} · ` : ''}<strong>{user.email}</strong></div>
        <button className="nav-logout" onClick={() => auth.signOut()}>Log out</button>
      </div>
    </nav>
  );
}

function Sidebar({ page, onNav, isCompanyAdmin, winsUnread }) {
  const items = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'assessment', label: 'Take Assessment' },
    { id: 'exam', label: 'Final Exam' },
    { id: 'workbooks', label: 'Workbooks' },
    { id: 'usecasefinder', label: 'AI Use Case Finder' },
    { id: 'promptlibrary', label: 'Prompt Library' },
    { id: 'agentlibrary', label: 'Agent Library' },
    { id: 'references', label: 'References' },
    { id: 'wins', label: 'Wins Wall' },
    { id: 'account', label: 'Account Settings' },
  ];

  if (isCompanyAdmin) {
    items.push({ id: 'teamdashboard', label: 'Team Dashboard' });
    items.push({ id: 'invites', label: 'Invite Participants' });
    items.push({ id: 'emailtemplates', label: 'Email Wording' });
    items.push({ id: 'resources', label: 'Resources Hub' });
  }

  return (
    <aside className="sidebar">
      {items.map(item => (
        <a
          key={item.id}
          href={`#/${item.id}`}
          className={`sidebar-item ${page === item.id || (page === 'workbook-detail' && item.id === 'workbooks') ? 'active' : ''}`}
          style={{ textDecoration: 'none' }}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            onNav(item.id);
          }}
        >
          <div className="sidebar-dot"></div>
          {item.label}
          {item.id === 'wins' && winsUnread > 0 && (
            <span
              title={`${winsUnread} new since your last visit`}
              style={{ marginLeft: 'auto', background: '#D4A847', color: '#1B2A4A', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '10px' }}
            >{winsUnread}</span>
          )}
        </a>
      ))}
    </aside>
  );
}

function PortalFooter({ onNav }) {
  return (
    <footer style={footerStyles.footer}>
      <span style={footerStyles.copy}>© {new Date().getFullYear()} The Corporate Fixers. All rights reserved.</span>
      <button onClick={() => onNav('privacy')} style={footerStyles.link}>Privacy Statement</button>
    </footer>
  );
}

const footerStyles = {
  footer: {
    width: '100%',
    padding: '16px 32px',
    borderTop: '1px solid #E8E6E2',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: 'DM Sans, sans-serif',
  },
  copy: {
    fontSize: '12px',
    color: '#8A8A8A',
  },
  link: {
    fontSize: '12px',
    color: '#1B2A4A',
    fontWeight: '600',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'DM Sans, sans-serif',
    textDecoration: 'underline',
  },
};
